import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { SignupContactAddressSection } from './SignupContactAddressSection'
import { SignupContactPhoneSection } from './SignupContactPhoneSection'
import type { SignupContactStepProps } from './SignupContactStep.types'

const ADDRESS_FIELDS = [
  'addressStreet',
  'addressStreetNumber',
  'addressCity',
  'addressPostalCode',
  'addressProvince',
  'addressCountry',
] as const

export const SignupContactStep = ({
  phoneSectionLabel,
  phonePrefixLabel,
  phonePrefixPlaceholder,
  phonePrefixSearchPlaceholder,
  phonePrefixNoResultsLabel,
  phonePrefixOptions,
  phoneNumberLabel,
  phonePlaceholder,
  residentialAddressLabel,
  contactDataSectionLabel,
  addressStreetLabel,
  addressStreetPlaceholder,
  addressStreetNumberLabel,
  addressStreetNumberPlaceholder,
  addressCityLabel,
  addressCityPlaceholder,
  addressPostalCodeLabel,
  addressPostalCodePlaceholder,
  addressProvinceLabel,
  addressProvincePlaceholder,
  addressCountryLabel,
  addressCountryPlaceholder,
  countrySearchPlaceholder,
  countryNoResultsLabel,
  addressCountryOptions,
  phonePrefix,
  phoneLocalNumber,
  addressStreet,
  addressStreetNumber,
  addressCity,
  addressPostalCode,
  addressProvince,
  addressCountry,
  onPhonePrefixChange,
  onPhoneLocalNumberChange,
  onAddressStreetChange,
  onAddressStreetNumberChange,
  onAddressCityChange,
  onAddressPostalCodeChange,
  onAddressProvinceChange,
  onAddressCountryChange,
  ui,
}: SignupContactStepProps) => {
  const [addressExpanded, setAddressExpanded] = useState(false)
  const hasAddressErrors = ADDRESS_FIELDS.some((field) => Boolean(ui.resolveFieldError(field)))
  const hasAddressData = Boolean(
    addressStreet.trim() ||
      addressStreetNumber.trim() ||
      addressCity.trim() ||
      addressPostalCode.trim() ||
      addressProvince.trim() ||
      addressCountry.trim(),
  )
  const showAddressSection = addressExpanded || hasAddressErrors || hasAddressData

  return (
    <div className="space-y-4">
      <SignupContactPhoneSection
        phoneSectionLabel={phoneSectionLabel}
        phonePrefixLabel={phonePrefixLabel}
        phonePrefixPlaceholder={phonePrefixPlaceholder}
        phonePrefixSearchPlaceholder={phonePrefixSearchPlaceholder}
        phonePrefixNoResultsLabel={phonePrefixNoResultsLabel}
        phonePrefixOptions={phonePrefixOptions}
        phoneNumberLabel={phoneNumberLabel}
        phonePlaceholder={phonePlaceholder}
        phonePrefix={phonePrefix}
        phoneLocalNumber={phoneLocalNumber}
        onPhonePrefixChange={onPhonePrefixChange}
        onPhoneLocalNumberChange={onPhoneLocalNumberChange}
        ui={ui}
      />

      <div className="space-y-2">
        {!showAddressSection ? (
          <>
            <button
              type="button"
              onClick={() => setAddressExpanded(true)}
              className="ca-btn-secondary inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium text-(--color-text-secondary)"
              aria-expanded={showAddressSection}
            >
              <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                <path d="M3 8h10M8 3v10" />
              </svg>
              {residentialAddressLabel}
            </button>
            <p className="text-[11px] text-(--color-text-muted)">{contactDataSectionLabel}</p>
          </>
        ) : null}

        <AnimatePresence initial={false}>
          {showAddressSection ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <SignupContactAddressSection
                residentialAddressLabel={residentialAddressLabel}
                contactDataSectionLabel={contactDataSectionLabel}
                addressStreetLabel={addressStreetLabel}
                addressStreetPlaceholder={addressStreetPlaceholder}
                addressStreetNumberLabel={addressStreetNumberLabel}
                addressStreetNumberPlaceholder={addressStreetNumberPlaceholder}
                addressCityLabel={addressCityLabel}
                addressCityPlaceholder={addressCityPlaceholder}
                addressPostalCodeLabel={addressPostalCodeLabel}
                addressPostalCodePlaceholder={addressPostalCodePlaceholder}
                addressProvinceLabel={addressProvinceLabel}
                addressProvincePlaceholder={addressProvincePlaceholder}
                addressCountryLabel={addressCountryLabel}
                addressCountryPlaceholder={addressCountryPlaceholder}
                countrySearchPlaceholder={countrySearchPlaceholder}
                countryNoResultsLabel={countryNoResultsLabel}
                addressCountryOptions={addressCountryOptions}
                addressStreet={addressStreet}
                addressStreetNumber={addressStreetNumber}
                addressCity={addressCity}
                addressPostalCode={addressPostalCode}
                addressProvince={addressProvince}
                addressCountry={addressCountry}
                onAddressStreetChange={onAddressStreetChange}
                onAddressStreetNumberChange={onAddressStreetNumberChange}
                onAddressCityChange={onAddressCityChange}
                onAddressPostalCodeChange={onAddressPostalCodeChange}
                onAddressProvinceChange={onAddressProvinceChange}
                onAddressCountryChange={onAddressCountryChange}
                ui={ui}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  )
}
